import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsOsMode } from "./OsContext";
import { useWindowId } from "./OsWindow";
import { useOpenUrl } from "./openUrl";
import { normalizeOsNavigateTarget } from "./urlMapping";

const MENU_WIDTH_PX = 192;
const MENU_HEIGHT_PX = 40;
const VIEWPORT_PADDING_PX = 8;

type MenuState = { x: number; y: number; to: string; title?: string };

export type OpenInNewWindowContextMenu = (
  event: React.MouseEvent,
  to: string,
  title?: string,
) => void;

/**
 * Right-click "Open in a new window" for OS target URLs.
 * Call `onContextMenu(event, url, title)` from any trigger and render `menu` once.
 * Outside OS mode (or with `enabled: false`) the native browser context menu is kept.
 */
export function useOpenInNewWindowMenu(options: { enabled?: boolean } = {}): {
  onContextMenu: OpenInNewWindowContextMenu;
  menu: React.ReactNode;
} {
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const enabled = (options.enabled ?? true) && (isOsMode || Boolean(windowId));
  const openUrl = useOpenUrl();
  const [state, setState] = useState<MenuState | null>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setState(null), []);

  useEffect(() => {
    if (!state) return;
    itemRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("blur", close);
    };
  }, [state, close]);

  const onContextMenu = useCallback<OpenInNewWindowContextMenu>(
    (event, to, title) => {
      if (!enabled || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      setState({
        x: Math.min(event.clientX, window.innerWidth - MENU_WIDTH_PX - VIEWPORT_PADDING_PX),
        y: Math.min(event.clientY, window.innerHeight - MENU_HEIGHT_PX - VIEWPORT_PADDING_PX),
        to,
        title,
      });
    },
    [enabled],
  );

  const menu =
    state && typeof document !== "undefined"
      ? createPortal(
          <div
            role="menu"
            className="fixed z-[99999] rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200 dark:bg-slate-700 dark:shadow-slate-900/50 dark:ring-slate-600"
            style={{ left: state.x, top: state.y, width: MENU_WIDTH_PX }}
            onPointerDown={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.preventDefault()}
          >
            <button
              type="button"
              role="menuitem"
              ref={itemRef}
              className="w-full rounded-lg px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-600 dark:focus:bg-slate-600"
              onClick={(event) => {
                event.stopPropagation();
                close();
                openUrl(normalizeOsNavigateTarget(state.to), {
                  title: state.title,
                  newWindow: true,
                });
              }}
            >
              Open in a new window
            </button>
          </div>,
          document.body,
        )
      : null;

  return { onContextMenu, menu };
}
